import {Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  IconAlertTriangle,
  IconCloudUpload,
  IconWifi,
  IconWifiOff,
} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useConnectivity} from 'src/offline/useConnectivity';
import {closeOfflineSession} from 'src/offline/offlineSessionManager';
import {retryItem} from 'src/offline/outbox';
import type {OfflineSession, OutboxItem} from 'src/offline/types';
import {discardItemAndDependents} from 'src/app/inspections/offline/outboxActions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface InspectionOfflinePanelProps {
  /** Session + counts owned by the page so a single subscription drives both the
   * page and this panel; this panel is a pure view. */
  session: OfflineSession | undefined;
  isDownloaded: boolean;
  pendingCount: number;
  failedCount: number;
  syncedCount: number;
  outboxItems: OutboxItem[];
  /** Sync controls owned by the page so the reconnect listener persists across
   * the execute→completed sub-view swap. */
  sync: () => Promise<void>;
  isSyncing: boolean;
}

const KIND_LABELS: Record<string, string> = {
  'inspection.recordDevice': 'Device recording',
  'inspection.addCustomDevice': 'Added device',
  'inspection.addAttachment': 'Photo',
  'inspection.transition': 'Inspection status',
};

const kindLabel = (kind: string): string => KIND_LABELS[kind] ?? kind;

export const InspectionOfflinePanel: React.FC<InspectionOfflinePanelProps> = ({
  session,
  isDownloaded,
  pendingCount,
  failedCount,
  syncedCount,
  outboxItems,
  sync,
  isSyncing,
}) => {
  const {setAlert} = useAlert();
  const {isOffline} = useConnectivity();
  const [itemToDiscard, setItemToDiscard] = useState<OutboxItem | undefined>();

  if (!session) return null;

  const failedItems = outboxItems.filter(item => item.status === 'failed');
  // Vacuously true for an idle downloaded session, so it can still be finished.
  const drained = outboxItems.every(item => item.status === 'succeeded');

  const handleRetry = async (item: OutboxItem) => {
    try {
      await retryItem(item.id);
      await sync();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDiscardConfirmed = async () => {
    if (!itemToDiscard) return;
    const item = itemToDiscard;
    setItemToDiscard(undefined);
    try {
      await discardItemAndDependents(item);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleClose = async () => {
    try {
      await closeOfflineSession(session.id);
      setAlert('Offline session finished.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <SectionCard title="Offline sync" icon={<IconCloudUpload size={16} />}>
      <Stack gap="md" p="md">
        <Group justify="space-between">
          <Group gap="sm">
            <Badge
              color={isOffline ? 'yellow' : 'green'}
              leftSection={
                isOffline ? <IconWifiOff size={12} /> : <IconWifi size={12} />
              }>
              {isOffline ? 'Offline' : 'Online'}
            </Badge>
            {pendingCount > 0 && (
              <Pill text={`${pendingCount} queued`} color="blue" />
            )}
            {syncedCount > 0 && (
              <Pill text={`${syncedCount} synced`} color="green" />
            )}
          </Group>
          {isDownloaded && pendingCount > 0 && (
            <Button
              color="yellow"
              loading={isSyncing}
              disabled={isOffline}
              leftSection={<IconCloudUpload size={16} />}
              onClick={() => sync()}>
              Sync now
            </Button>
          )}
        </Group>

        {failedItems.length > 0 && (
          <Stack gap="xs">
            <Group gap={6}>
              <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
              <Text size="sm" fw={600}>
                {failedItems.length} change
                {failedItems.length === 1 ? '' : 's'} need attention
              </Text>
            </Group>
            {failedItems.map(item => (
              <Alert key={item.id} color="red" p="xs">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {kindLabel(item.kind)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {item.error ?? 'Sync failed.'}
                    </Text>
                  </Stack>
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      variant="outline"
                      size="xs"
                      disabled={isOffline}
                      onClick={() => handleRetry(item)}>
                      Retry
                    </Button>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => setItemToDiscard(item)}>
                      Discard
                    </Button>
                  </Group>
                </Group>
              </Alert>
            ))}
          </Stack>
        )}

        {drained && (
          <Button variant="outline" onClick={handleClose}>
            Finish offline session
          </Button>
        )}

        {failedCount === 0 && pendingCount > 0 && (
          <Text size="xs" c="dimmed">
            {pendingCount} change{pendingCount === 1 ? '' : 's'} will sync when
            you reconnect.
          </Text>
        )}
      </Stack>

      <ConfirmDialog
        opened={itemToDiscard !== undefined}
        onClose={() => setItemToDiscard(undefined)}
        onConfirm={handleDiscardConfirmed}
        title="Discard this change?"
        body={`Discarding this ${itemToDiscard ? kindLabel(itemToDiscard.kind).toLowerCase() : 'change'} permanently removes it from this device — it will not be saved.`}
        confirmationButtonColor="red"
        buttonTextYes="Discard"
        buttonTextNo="Keep"
      />
    </SectionCard>
  );
};
